import React, { Component } from 'react';
import PropTypes from 'prop-types';
import TagCloud from '../TagCloud/TagCloud';
import AssetValidityFormComponent from '../AssetValidityFormComponent/AssetValidityFormComponent';

export default class SignForm extends Component {
  state = {
    showErrors: false,
    errors: {},
    name: this.props.sign?.name || '',
    description: this.props.sign?.description || '',
  };

  UNSAFE_componentWillReceiveProps(nextProps) {
    const { showErrors } = this.state;

    if (showErrors) {
      this.validate(nextProps);
    }
  }

  componentDidUpdate(prevProps) {
    const { sign } = this.props;

    if (sign && sign !== prevProps.sign) {
      this.setState({
        name: sign.name || '',
        description: sign.description || '',
      });
    }
  }

  /**
   * Handles changes to the start date, ensuring it is valid and properly formatted before passing it to the parent component.
   * @param {Date} date
   */
  changeStartTs = date => {
    const { onChange } = this.props;

    onChange('validityStart', date.toISOString());
  };

  /**
   * Handles changes to the end date, ensuring it is valid and properly formatted before passing it to the parent component.
   * @param {Date} date
   */
  changeEndTs = date => {
    const { onChange } = this.props;

    onChange('validityEnd', date.toISOString());
  };

  /**
   * Handles changes to the validity enabled checkbox, passing the new state to the parent component.
   * @param {boolean} enabled
   */
  setValidity = enabled => {
    const { onChange } = this.props;

    onChange('validityEnabled', enabled);
  };

  onChange = event => {
    const { onChange } = this.props;

    onChange(event.target.name, event.target.value);
  };

  onChangeTag = tags => {
    const { onChange } = this.props;

    onChange('tags', tags);
  };

  errorClass = error => (error ? 'error' : null);

  validate = nextProps => {
    const props = nextProps || this.props;
    const {
      sign: { name },
    } = props;
    const errors = {};

    if (!name || !name.trim()) {
      errors.name = 'This field cannot be empty.';
    } else if (!this.isUniqueName(props)) {
      errors.name = 'This name is already in use.';
    }

    this.setState({ showErrors: true, errors });

    return !errors.name;
  };

  isUniqueName = props => {
    const { signs, sign } = props;

    return !signs.some(item => {
      const name = sign.name.toLowerCase().trim();
      const itemName = item.name.toLowerCase().trim();

      return +item.id !== +sign.id && itemName === name;
    });
  };

  submit = event => {
    const { onUpdate } = this.props;

    event.preventDefault();

    if (this.validate()) {
      onUpdate();
    }
  };

  render() {
    const {
      auth: {
        user: {
          permissions: { Writer },
        },
      },
      sign,
      onCancel,
    } = this.props;
    const { name, description, errors } = this.state;

    if (!sign) {
      return null;
    }

    return (
      <div
        id="edit"
        className="form-signs lg:col-span-8 mt-8 bg-white rounded-[2.5rem] shadow-[0_20px_50px_rgba(0,0,0,0.05)] border border-gray-100 p-4"
      >
        <form className="space-y-6" onSubmit={this.submit}>
          <div className="grid grid-cols-2 gap-4">
            {this.errorClass(errors.name) && (
              <span className="text-sunrise font-bold mt-1 mb-1">{errors.name}</span>
            )}
            <div className="relative group" data-test="signform-form-group">
              <label
                htmlFor="sign-input-name"
                className="cursor-pointer form-label text-[10px] font-black uppercase tracking-[0.2em] text-gray-400 absolute -top-2 left-6 bg-white px-2 transition-all group-focus-within:text-warmth"
              >
                Sign Name
              </label>
              <input
                id="sign-input-name"
                type="text"
                data-test="input-name"
                disabled={!Writer}
                name="name"
                value={name}
                onChange={e => this.setState({ name: e.target.value })}
                onBlur={e => this.onChange(e)}
                onKeyDown={e => {
                  if (e.key === 'Enter') {
                    e.stopPropagation();
                    this.onChange(e);
                  }
                }}
                className="w-full text-xl font-bold px-2 py-3 rounded-2xl border border-gray-100 focus:border-warmth focus:ring-0 transition-all outline-none"
              />
            </div>

            <div className="relative group" data-test="signform-form-group">
              <label
                htmlFor="sign-input-description"
                className="cursor-pointer text-[10px] font-black uppercase tracking-[0.2em] text-gray-400 absolute -top-2 left-6 bg-white px-2 transition-all group-focus-within:text-warmth"
              >
                Description
              </label>
              <textarea
                rows="1"
                data-test="input-desc"
                id="sign-input-description"
                disabled={!Writer}
                value={description}
                onInput={e => {
                  e.target.style.height = 'auto';
                  e.target.style.height = `${e.target.scrollHeight}px`;
                }}
                placeholder="Enter description"
                name="description"
                onChange={e => this.setState({ description: e.target.value })}
                onBlur={e => this.onChange(e)}
                onKeyDown={e => {
                  if (e.key === 'Enter') {
                    e.stopPropagation();
                    this.onChange(e);
                  }
                }}
                className=" w-full text-xl font-medium px-2 py-3 rounded-2xl border border-gray-100 focus:border-warmth focus:ring-0 transition-all outline-none resize-none"
              />
            </div>

            <TagCloud tags={sign.tags} onChange={this.onChangeTag} disabled={!Writer} />
          </div>
          <AssetValidityFormComponent
            enabled={Boolean(sign.validityEnabled)}
            setValidity={this.setValidity}
            validityStart={new Date(sign.validityStart)}
            changeStartTs={this.changeStartTs}
            validityEnd={new Date(sign.validityEnd)}
            changeEndTs={this.changeEndTs}
          />
          <div className="pt-6 flex flex-col sm:flex-row gap-4">
            {Writer && (
              <button
                data-test="button-update"
                type="submit"
                className="flex-1 bg-btn-primary-form text-white px-8 py-4 rounded-2xl font-bold uppercase tracking-widest text-xs hover:scale-[1.02] active:scale-[0.98] transition-all shadow-lg shadow-black/20"
              >
                Save Changes
              </button>
            )}
            <button
              data-test="button-cancel"
              type="button"
              className="px-8 py-4 rounded-2xl font-bold uppercase tracking-widest text-xs text-gray-400 hover:text-black transition-colors border border-transparent hover:border-gray-100"
              onClick={onCancel}
            >
              Cancel
            </button>
          </div>
        </form>
      </div>
    );
  }
}

SignForm.propTypes = {
  auth: PropTypes.object.isRequired,
  signs: PropTypes.array,
  sign: PropTypes.object,
  onChange: PropTypes.func.isRequired,
  onUpdate: PropTypes.func.isRequired,
  onCancel: PropTypes.func.isRequired,
};
